// PRD.324 — measuring a Rich Text document for pagination.
//
// The layout rules live in `layoutRichTextFlow` (pure, unit-tested). This is
// the only piece that genuinely needs a DOM: how tall does each block render at
// the mapping's actual width?
//
// It renders the document offscreen at the exact target pixel width, measures
// each block, and reports heights as a PERCENTAGE OF PAGE HEIGHT — the same
// unit every mapping already uses, so the engine needs no conversion.
//
// Measuring BLOCKS rather than the whole document is what lets pagination break
// between paragraphs and list items instead of mid-line.

import { useEffect, useRef } from 'react';
import { type FlowBlock, type RichTextDoc } from '@helios/data-access';
import { RichTextView } from './RichTextField';

/**
 * Heights of each rendered LINE inside one block.
 *
 * Walks the block's text nodes and asks the browser for the client rects of a
 * Range covering them: one rect per line box, so the result reflects actual
 * wrapping, bold/italic metrics and list indentation. Rects sharing a `top` are
 * one visual line (a line can span several inline elements).
 *
 * Returns `undefined` when the block has no text to split — the caller then
 * treats it as unsplittable and places it whole rather than dropping it.
 */
function measureLineHeights(el: HTMLElement, pageHeightPx: number): number[] | undefined {
  if (typeof document === 'undefined' || typeof Range === 'undefined') return undefined;
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  const rows: { top: number; bottom: number }[] = [];

  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const text = node.textContent ?? '';
    if (!text.trim()) continue;
    const range = document.createRange();
    range.selectNodeContents(node);
    for (const rect of Array.from(range.getClientRects())) {
      if (rect.height <= 0) continue;
      // Same line box if it shares a top edge within a rounding tolerance.
      const row = rows.find((r) => Math.abs(r.top - rect.top) < 1);
      if (row) {
        row.bottom = Math.max(row.bottom, rect.bottom);
      } else {
        rows.push({ top: rect.top, bottom: rect.bottom });
      }
    }
  }

  if (rows.length <= 1) return undefined;
  rows.sort((a, b) => a.top - b.top);
  return rows.map((r) => ((r.bottom - r.top) / pageHeightPx) * 100);
}

export function RichTextFlowMeasurer({
  doc,
  widthPx,
  pageHeightPx,
  onMeasured,
}: {
  doc: RichTextDoc;
  /** Rendered width of the mapping box, in CSS pixels. */
  widthPx: number;
  /** Rendered height of one page, in CSS pixels. */
  pageHeightPx: number;
  onMeasured: (blocks: FlowBlock[]) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || widthPx <= 0 || pageHeightPx <= 0) return;
    let cancelled = false;

    const measure = () => {
      if (cancelled || !ref.current) return;
      const children = Array.from(ref.current.children) as HTMLElement[];
      onMeasured(
        children.map((child, index) => ({
          index,
          heightPct: (child.getBoundingClientRect().height / pageHeightPx) * 100,
          // Per-LINE heights, needed only when a single block turns out to be
          // taller than a page. Real rendered line boxes via Range client
          // rects — never a characters-per-line estimate, which cannot know
          // the font, the marks or the wrap width.
          lineHeightsPct: measureLineHeights(child, pageHeightPx),
        })),
      );
    };

    // Web fonts change wrapping, so measuring before they settle would
    // paginate against one typography and render with another. `fonts.ready`
    // resolves immediately once they are loaded.
    if (typeof document !== 'undefined' && document.fonts?.ready) {
      void document.fonts.ready.then(measure);
    } else {
      measure();
    }
    return () => {
      cancelled = true;
    };
    // `doc` is compared by identity; callers memoize it from the stored string.
  }, [doc, widthPx, pageHeightPx, onMeasured]);

  return (
    <div
      ref={ref}
      aria-hidden
      style={{
        position: 'absolute',
        // Offscreen rather than `display: none`, which would report zero
        // heights, and rather than `visibility: hidden`, which would still
        // occupy layout space in the page.
        left: -99999,
        top: 0,
        width: widthPx,
        pointerEvents: 'none',
      }}
    >
      {/* One wrapper per block, so each is measured independently at the same
          width and typography the real render uses. */}
      {doc.blocks.map((block, i) => (
        <div key={i}>
          <RichTextView doc={{ version: 1, blocks: [block] }} />
        </div>
      ))}
    </div>
  );
}
